<template>
  <div class="flex flex-col grow overflow-y-auto">
    <PassportHero
      variant="asset"
      :name="item.name"
      :identifier="item.identifier"
      :imageUrl="item.thumbnailUrl"
      :readonly="!canUpdate"
      @update="$emit('update', $event)"
      @goToStep="$emit('goToStep', $event)"
    >
      <PassportInsights
        type="tasks"
        :tooltip="$t('tooltips.asset_passport.task_insights')"
        :count="item.tasks_open + item.tasks_scheduled"
        :secondaryCount="item.overdue_tasks_count"
        @click="$emit('goToStep', PropertyType.TASKS)"
      />
      <PassportInsights
        type="files"
        :count="item.files.length"
        :secondaryCount="item.expired_files_count"
        :class="
          hasPermission(permissions.readDocument, building.id)
            ? 'cursor-pointer'
            : 'pointer-events-none'
        "
        @click="$emit('goToStep', PropertyType.FILES)"
      />
    </PassportHero>
    <div
      class="flex flex-col gap-4 overflow-y-auto"
      :class="{
        'p-4 pb-6': viewport !== 'desktop',
        'p-6 pb-4': viewport === 'desktop',
        'lg:p-6 lg:pb-4': viewport === 'reactive',
      }"
    >
      <PassportNav>
        <PassportNavItem
          variant="information"
          :label="$t('components.passport.information.label')"
          @navigate="$emit('goToStep', 'info')"
        />
      </PassportNav>
      <PassportNav>
        <PassportNavItem
          variant="space"
          :label="item.space?.name"
          :placeholder="$t('components.passport.space.placeholder')"
          :disabled="!canUpdate"
          @navigate="$emit('goToStep', PropertyType.SPACE)"
        />
        <PassportNavItem
          variant="asset_group"
          :label="item.group?.name"
          :placeholder="$t('components.passport.group.placeholder')"
          :disabled="!canUpdate"
          @navigate="$emit('goToStep', PropertyType.ASSET_GROUP)"
        />
      </PassportNav>
      <PassportNav>
        <PassportNavItem
          variant="qr-code"
          :label="$t('components.passport.qr_codes.label', 2)"
          :count="item.qr_codes.length"
          @navigate="
            item.qr_codes.length > 0 || !runningInWebView()
              ? goToQrCodes()
              : openQrCodeScanner()
          "
        />
      </PassportNav>
      <div class="border border-secondary rounded-lg bg-primary py-0.5">
        <AssetTags
          :asset="item"
          :initialState="item.tags"
          variant="tags"
          :readonly="!canUpdate"
          @update="$emit('update', $event)"
          @update:item="$emit('update:item', $event)"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, inject, onMounted, onUnmounted } from "vue";
import {
  PassportHero,
  PassportInsights,
  PassportNav,
  PassportNavItem,
} from "@/SharedComponents/Passport";
import AssetTags from "@/SharedComponents/Passport/Steps/Tags/AssetTags";
import { PropertyType } from "@/Composables/useProperties";
import { useQrCodeScanner } from "@/Composables/useQrCodeScanner";

const emit = defineEmits(["goToStep", "update", "update:item", "reload:item"]);

const props = defineProps({
  item: { type: Object, required: true },
  building: { type: Object, required: true },
  canUpdate: { type: Boolean, required: false },
  scanned: { type: Boolean, required: false },
  viewport: {
    type: String,
    validator: (val) => ["mobile", "desktop", "reactive"].includes(val),
    default: "reactive",
  },
});

const { hasPermission, permissions } = inject("permissions");

function goToQrCodes() {
  emit("goToStep", PropertyType.QR_CODES);
}

const { runningInWebView, getWebViewBridge, openQrCodeScanner, updateQrCode } =
  useQrCodeScanner(props.item, (item) => {
    emit("update:item", {
      item,
    });
    goToQrCodes();
  });

onMounted(() => {
  emit("reload:item");

  if (runningInWebView()) {
    getWebViewBridge().addEventListener("onQrCodeScanned", updateQrCode);
  }
});

onUnmounted(() => {
  if (runningInWebView()) {
    getWebViewBridge().removeEventListener("onQrCodeScanned", updateQrCode);
  }
});

defineExpose({
  editing: computed(() => props.scanned),
});
</script>
